import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, mergeMap, tap } from 'rxjs/operators';
import { SpinnerService } from 'src/app/shared/components/spinner/spinner.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { AppLocalStorageService } from 'src/app/shared/services/app-local-storage.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AppHttpInterceptor implements HttpInterceptor {
  authService = inject(AuthService);
  notificationService = inject(NotificationService);
  spinnerService = inject(SpinnerService);
  router = inject(Router);
  appLocalStorageService = inject(AppLocalStorageService);

  // Número de requisições para disparar verificação de novas notificações
  private readonly NOTIFICATION_REQUEST_THRESHOLD = 20;
  private readonly MAX_SESSION_INVALIDATION_ERROR_BYTES = 4096;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Extrai os controles do spinner e env
    const spinnerDelay = parseInt(req.params.get('spinnerDelay') ?? '0');
    const spinnerOff = req.params.get('spinnerOff')?.toLocaleLowerCase() === 'true';
    const removeEnv = req.params.get('removeEnv')?.toLocaleLowerCase() === 'true';

    // Remove os controles do spinner e env
    let params = req.params.delete('spinnerDelay').delete('spinnerOff').delete('removeEnv');

    // Acrescenta env=dev quando o ambiente for desenvolvimento/homologação
    if (!environment.production && !params.has('env') && (req.url.includes('bombeiros.to.gov.br') || req.url.includes('localhost')) && !removeEnv) {
      params = params.set('env', 'dev');
    }
    // Acrescenta origin=manutencao quando a requisição é feita a partir do módulo de manutenção
    if (this.router.url.startsWith('/manutencao') && !params.has('origin')) {
      params = params.set('origin', 'manutencao');
    }

    // Clona a requisição com os novos parâmetros
    const newReq = req.clone({ params });

    // Indica se a requisição é de notificações
    const isNotificacaoRequest = req.url.includes('/notificacao');

    // Verifica se o token não expirou
    const tokenValido = this.authService.isTokenValid();
    const isRotaPublica = this.authService.isRotaPublica();

    if (!tokenValido && !isRotaPublica) {
      this.authService.tratarSessaoInvalida();
      return EMPTY;
    }

    !spinnerOff && setTimeout(() => this.spinnerService.start(), 0);

    return next.handle(newReq).pipe(
      catchError((error: HttpErrorResponse) => {
        const isRotaPublica = this.authService.isRotaPublica();
        // Verifica se é invalidação de sessão (403 com flag especial)
        if (error.status === 403 && error.error?.session_invalidated && !isRotaPublica) {
          this.authService.tratarSessaoInvalida(error.error.msg);
          return EMPTY;
        }
        if (error.status === 403 && error.error instanceof Blob && !isRotaPublica && this.isJsonBlob(error.error)) {
          return this.handleBlobSessionInvalidation(error);
        }
        // Verifica se o erro é 401 (Unauthorized)
        if (error.status === 401 && !isRotaPublica) {
          this.authService.tratarSessaoInvalida();
          return EMPTY;
        }
        return throwError(() => error);
      }),
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse && event.status >= 200 && event.status < 300) {
          if (isNotificacaoRequest) {
            // Quando a requisição é para /notificacao, zera o contador
            this.appLocalStorageService.setItem('requestCounter', '0');
          } else {
            let counter = parseInt(this.appLocalStorageService.getItem('requestCounter') || '0', 10);
            counter++;
            if (counter >= this.NOTIFICATION_REQUEST_THRESHOLD) {
              this.notificationService.sendGetNotification(true);
              counter = 0;
            }
            this.appLocalStorageService.setItem('requestCounter', counter.toString());
          }
        }
      }),
      finalize(() => !spinnerOff && this.spinnerService.finish(spinnerDelay))
    );
  }

  private handleBlobSessionInvalidation(error: HttpErrorResponse): Observable<never> {
    const errorBlob = error.error as Blob;
    if (errorBlob.size > this.MAX_SESSION_INVALIDATION_ERROR_BYTES) return throwError(() => error);

    return from(errorBlob.text()).pipe(
      catchError(() => of('')),
      mergeMap(content => {
        const payload = this.parseSessionInvalidationPayload(content);
        if (!payload) return throwError(() => error);

        this.authService.tratarSessaoInvalida(payload.message);
        return EMPTY;
      })
    );
  }

  private isJsonBlob(blob: Blob): boolean {
    const mimeType = blob.type.split(';')[0].trim().toLowerCase();
    return mimeType === 'application/json' || mimeType.endsWith('+json');
  }

  private parseSessionInvalidationPayload(content: string): SessionInvalidationPayload | null {
    try {
      const parsed = JSON.parse(content) as Record<string, unknown>;
      if (parsed?.['session_invalidated'] !== true) return null;

      const rawMessage = parsed['msg'];
      const message = typeof rawMessage === 'string' && rawMessage.length <= 300 && !/[\u0000-\u001f\u007f]/.test(rawMessage) ? rawMessage.trim() : '';
      return { message };
    } catch {
      return null;
    }
  }
}

interface SessionInvalidationPayload {
  message: string;
}
